import { useEffect, useState, Fragment } from "react";
import { toast } from "sonner";
import { Info, Check, X, ShoppingCart, CheckCheck, Printer, RefreshCw, Sparkles, Building2 } from "lucide-react";
import { api, nzd, num } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Badge, Loading } from "@/components/kit";

export default function Purchasing() {
  const { activeStore } = useAuth();
  const [data, setData] = useState(null);
  const [rows, setRows] = useState({});
  const [openWhy, setOpenWhy] = useState(null);
  const [ordering, setOrdering] = useState(false);

  const load = () => {
    if (!activeStore) return;
    setData(null);
    api.get("/purchasing", { params: { store_id: activeStore } }).then(({ data }) => {
      setData(data);
      const init = {};
      data.recommendations.forEach((r) => (init[r.product_id] = { qty: r.recommended, status: "pending" }));
      setRows(init);
    });
  };
  useEffect(load, [activeStore]);

  if (!data) return <Loading label="Calculating supplier order recommendations…" />;

  const setQty = (pid, v) => setRows({ ...rows, [pid]: { ...rows[pid], qty: v } });
  const setStatus = (pid, status) => setRows({ ...rows, [pid]: { ...rows[pid], status } });

  const acceptAll = () => {
    const updated = { ...rows };
    data.recommendations.forEach((r) => {
      updated[r.product_id] = { ...updated[r.product_id], status: "accepted" };
    });
    setRows(updated);
    toast.success("All supplier recommendations accepted");
  };

  const ignoreAll = () => {
    const updated = { ...rows };
    data.recommendations.forEach((r) => {
      updated[r.product_id] = { ...updated[r.product_id], status: "ignored" };
    });
    setRows(updated);
  };

  const placeOrder = async () => {
    const items = Object.entries(rows)
      .filter(([, v]) => v.status === "accepted" && Number(v.qty) > 0)
      .map(([product_id, v]) => ({ product_id, quantity: Number(v.qty) }));
    if (!items.length) return toast.error("Accept at least one recommendation first");

    setOrdering(true);
    try {
      const { data: res } = await api.post("/purchasing/order", { store_id: activeStore, items });
      toast.success(`Purchase order generated for ${items.length} item(s)!`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create order");
    } finally {
      setOrdering(false);
    }
  };

  const totalEstCost = data.recommendations.reduce((acc, r) => {
    const row = rows[r.product_id];
    if (row && row.status === "accepted") {
      const qty = Number(row.qty) || 0;
      const unitCost = r.recommended > 0 ? r.cost_estimate / r.recommended : 0;
      return acc + qty * unitCost;
    }
    return acc;
  }, 0);

  const acceptedCount = Object.values(rows).filter((v) => v.status === "accepted").length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Smart Purchasing & POs"
        subtitle="Order recommendations"
        right={
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-700 px-3 py-2 rounded-xl text-xs font-semibold shadow-2xs transition-colors"
            >
              <Printer className="h-3.5 w-3.5" /> Print Order Sheet
            </button>
            <button
              data-testid="place-order-btn"
              disabled={ordering}
              onClick={placeOrder}
              className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white px-4 py-2 rounded-xl text-xs font-semibold transition-all shadow-md"
            >
              {ordering ? (
                <>
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Processing…
                </>
              ) : (
                <>
                  <ShoppingCart className="h-3.5 w-3.5 text-amber-400" />
                  Generate PO ({acceptedCount} items · {nzd(totalEstCost)})
                </>
              )}
            </button>
          </div>
        }
      />

      <div className="bg-white border border-zinc-200/90 rounded-2xl p-4 shadow-2xs flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">Bulk Actions:</span>
          <button
            onClick={acceptAll}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100/80 border border-emerald-200 px-3 py-1.5 rounded-xl transition-colors"
          >
            <CheckCheck className="h-3.5 w-3.5" /> Accept All Recommendations
          </button>
          <button
            onClick={ignoreAll}
            className="text-xs font-semibold text-zinc-500 hover:text-zinc-700 bg-zinc-100 hover:bg-zinc-200/80 px-3 py-1.5 rounded-xl transition-colors"
          >
            Reset / Ignore All
          </button>
        </div>

        <div className="text-xs text-zinc-500">
          Total Estimated Purchase: <strong className="text-zinc-900 font-bold">{nzd(totalEstCost)}</strong>
        </div>
      </div>

      {/* Prep for Tomorrow — what the kitchen needs ready before opening */}
      {data.prep_for_tomorrow && (
        <Card className="bg-amber-50/60 border-amber-200/60 overflow-hidden" testid="prep-for-tomorrow">
          <div className="px-6 py-3 border-b border-amber-200/50 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-600" />
            <h3 className="font-medium text-zinc-900 text-sm">Prep for Tomorrow</h3>
            <span className="text-xs text-amber-700">Based on today's forecast</span>
          </div>
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(data.prep_for_tomorrow).map(([name, info]) => (
              <div key={name} className="bg-white border border-amber-200/40 rounded-lg px-3 py-3 shadow-xs">
                <div className="text-sm font-medium text-zinc-900">{name}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{info.reason}</div>
                <div className="mt-1.5 font-display font-semibold text-amber-700 text-base tnum">{num(info.required, 1)} {info.unit}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="overflow-hidden" testid="purchasing-table">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left uppercase tracking-wider text-zinc-400 border-b border-zinc-200 bg-zinc-50/50">
                <th className="px-5 py-3.5 font-semibold">Product / Item</th>
                <th className="px-5 py-3.5 font-semibold text-right">On Hand</th>
                <th className="px-5 py-3.5 font-semibold text-right">Forecast Need</th>
                <th className="px-5 py-3.5 font-semibold text-right">Recommended Order</th>
                <th className="px-5 py-3.5 font-semibold text-right">Supplier</th>
                <th className="px-5 py-3.5 font-semibold text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.recommendations.map((r) => {
                const row = rows[r.product_id] || {};
                const isAccepted = row.status === "accepted";
                const isIgnored = row.status === "ignored";

                return (
                  <Fragment key={r.product_id}>
                    <tr className="order-b border-zinc-50 transition-colors">
                      <td className="px-5 py-3.5 font-semibold text-zinc-900">
                        {r.product} {row.status === "ordered" && <Badge level="success">Ordered</Badge>}
                      </td>
                      <td className="px-5 py-3.5 text-right tnum text-zinc-500">
                        {num(r.current_stock, 1)} {r.unit}
                      </td>
                      <td className="px-5 py-3.5 text-right tnum text-zinc-700">
                        {num(r.forecast_demand, 1)} {r.unit}
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <div className="inline-flex items-center gap-1 justify-end">
                          <input
                            type="number"
                            data-testid="qty-input"
                            value={row.qty ?? r.recommended}
                            onChange={(e) => setQty(r.product_id, e.target.value)}
                            className="w-20 text-right tnum border border-zinc-200 bg-white rounded-lg px-2 py-1 font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900"
                          />
                          <span className="text-zinc-400 w-8 text-left">{r.unit}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-zinc-600">
                        <span className="font-medium text-zinc-800">{r.supplier}</span>{" "}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            data-testid="accept-btn"
                            title="Accept recommendation"
                            onClick={() => setStatus(r.product_id, isAccepted ? "pending" : "accepted")}
                            className="p-1.5 rounded-lg transition-colors hover:bg-emerald-50 text-emerald-600"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            data-testid="ignore-btn"
                            title="Ignore recommendation"
                            onClick={() => setStatus(r.product_id, isIgnored ? "pending" : "ignored")}
                            className="p-1.5 rounded-lg transition-colors hover:bg-rose-50 text-rose-500"
                          >
                            <X className="h-4 w-4" />
                          </button>
                          <button
                            data-testid="why-btn"
                            title="Why this amount?"
                            onClick={() => setOpenWhy(openWhy === r.product_id ? null : r.product_id)}
                            className="p-1.5 rounded-lg transition-colors hover:bg-zinc-100 text-zinc-500"
                          >
                            <Info className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {openWhy === r.product_id && (
                      <tr className="border-b border-zinc-100 bg-zinc-50/40">
                        <td colSpan={8} className="px-5 py-3 text-xs text-amber-900 border-b border-amber-200/60">
                          <span className="font-bold flex items-center gap-1 mb-0.5">
                            <Sparkles className="h-3.5 w-3.5 text-amber-600" /> Model Reasoning:
                          </span>
                          {r.explanation || r.reasoning}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
